import { useAssign } from "./basic"
import { useLazyQuery, useMutation, useQuery } from "@apollo/react-hooks"
import { TableHookProps, TableProps } from "Types/table"
import { useState } from "react"
import { ApolloError } from "apollo-client"

// 表格通用
export function useTable<T>(tableProps: TableProps) {
    const {
        name, initVariables, getQuery,
        addMutation, updateMutation, delMutation,
    } = tableProps
    const { object: variables, handleChange: handleSetVariables } = useAssign(initVariables)
    const [ fetchData, { data, loading, refetch }] = useLazyQuery(getQuery, {
        notifyOnNetworkStatusChange: true, // 设置loading，error等状态在refetch时会更新
        fetchPolicy: "network-only", // 始终从服务器端获取数据
        variables,
    })
    // 数据条目总数
    const { aggregate = {} } = data ? data[`${name}_aggregate`] : {}
    const { count } = aggregate
    const [ add ] = useMutation(addMutation)
    const [ update ] = useMutation(updateMutation)
    const [ del ] = useMutation(delMutation)
    type func = (newData: T) => void
    // 添加条目
    const addItem: func = async (newData: T) => {
        await add({ variables: newData})
        await refetch()
    }
    interface IdWise {
        id?: number,
        uid?: string
    }
    // 更新条目
    const updateItem = async <U extends IdWise>(updateData: U) => {
        await update({variables: updateData})
        await refetch()
    }
    // 删除条目
    const delItem = async <U extends IdWise>(delData: U) => {
        await del({variables: delData})
        await refetch()
    }
    // 翻页
    const handleChangePage = (page: number) => {
        handleSetVariables({
            offset: page * variables.limit,
        })
    }
    const tableData = data ? data[name] : []
    return {
        fetchData, tableData, loading,
        refetch, variables, handleSetVariables,
        totalCount: count || 0,
        addItem, updateItem, delItem,
        handleChangePage,
    }
}
// 老表格
export const useOldTable = (props: TableHookProps) => {
    const {
        tableName, initVariables, getQuery, addMutation,
        updateMutation, delMutation, field, direction = "last" } = props
    const [ variables, setVariables ] = useState(initVariables)
    const { loading, data, refetch } = useQuery(getQuery, {
        notifyOnNetworkStatusChange: true,
        variables,
        fetchPolicy: "network-only",
    })
    const { aggregate = {} } = data ? data[`${tableName}_aggregate`] : {}
    const { count } = aggregate || {}
    const [ add ] = useMutation(addMutation, {
        update: (cache, { data }) => {
            const cacheData: any = cache.readQuery({
                query: getQuery,
                variables,
            }) || {}
            const addData = data ? data[`insert_${tableName}`].returning : []
            cacheData[`${tableName}_aggregate`].aggregate.count++
            cache.writeQuery({
                data: {
                    ...cacheData,
                    [tableName]: direction === "last"
                        ? cacheData[tableName].concat(addData) : addData.concat(cacheData[tableName]),
                },
                query: getQuery,
                variables,
            })
        },
        onError: (error: ApolloError) => {
            throw error
        },
    })
    const [ update ] = useMutation(updateMutation, {
        update: (cache, {data}) => {
            const cacheData: any = cache.readQuery({
                query: getQuery,
                variables,
            }) || {}
            const updateData = data ? data[`update_${tableName}`].returning : []
            cache.writeQuery({
                data: {
                    ...cacheData,
                    [tableName]: cacheData[tableName].map((c: any) => {
                        const key = tableName === "users" ? "uid" : "id"
                        if (c[key] === updateData[0][key]) {
                            return updateData[0]
                        } else {
                            return c
                        }
                    }),
                },
                query: getQuery,
                variables,
            })
        },
    })
    const [ del ] = useMutation(delMutation, {
        update: (cache, { data }) => {
            const cacheData: any = cache.readQuery({
                query: getQuery,
                variables,
            }) || {}
            const deleteData = data ? data[`delete_${tableName}`].returning : []
            cacheData[`${tableName}_aggregate`].aggregate.count--
            const key = tableName === "users" ? "uid" : "id"
            cache.writeQuery({
                data: {
                    ...cacheData,
                    [tableName]: cacheData[tableName].filter((d: any) => d[key] !== deleteData[0][key]),
                },
                query: getQuery,
                variables,
            })
        },
    })

    const onRowAdd: (newData: any) => Promise<void>  = (newData: any) => new Promise((resolve) => {
        add({ variables: {...newData}}).then(() => resolve())
    })
    const onRowUpdate: (newData: any) => Promise<void> = (newData: any) => new Promise((resolve) => {
        let updateData = {}
        field.forEach((f: string) => updateData = {...updateData, [f]: newData[f]})
        update({variables: { ...updateData }}).then(() => resolve())
    })
    const onRowDelete: (newData: any) => Promise<void> = (oldData: any) => new Promise((resolve) => {
        const key = tableName === "users" ? "uid" : "id"
        del({variables: {[key]: oldData[key]}}).then(() => resolve())
    })
    const onChangePage = (page: number) => {
        setVariables({
            ...variables,
            offset: page * variables.limit,
        })
    }
    return {
        data: data ? data[tableName] : [],
        loading,
        onChangePage,
        onRowAdd,
        onRowDelete,
        onRowUpdate,
        setVariables,
        totalCount: count || 0,
        variables,
        refetch,
    }
}
